import {useEffect, useState} from 'react'
import {useLayout} from '../../../../_metronic/layout/core'
import {
  createStockAssignment,
  getWarehouseOptions,
  getProductOptions,
  WarehouseOption,
  ProductOption,
} from '../api'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import toast from 'react-hot-toast'
import Select from 'react-select'
import {KTCard, KTCardBody} from '../../../../_metronic/helpers'
import {removeIndicatorSeparator, solidSelectStyles} from '../../shared/ui/selectStyles'

// ─── Validation Schema ────────────────────────────────────────────────────────

const initialAssignmentSchema = Yup.object().shape({
  warehouse_id: Yup.number().min(1, 'Warehouse is required').required('Warehouse is required'),
  product_id: Yup.number().min(1, 'Product is required').required('Product is required'),
  quantity: Yup.number().min(1, 'Quantity must be at least 1').required('Quantity is required'),
  price_per_unit: Yup.number().min(0),
  ref_doc_no: Yup.string(),
  addition_date: Yup.string().required('Date of addition is required'),
})

// ─── Types ────────────────────────────────────────────────────────────────────

interface InitialAssignmentFormValues {
  warehouse_id: number
  product_id: number
  quantity: number
  price_per_unit: number
  ref_doc_no: string
  addition_date: string
}

type OptionType = {
  value: number
  label: string
}

const getTodayDate = () => new Date().toISOString().split('T')[0]

// ─── Component ────────────────────────────────────────────────────────────────

export const InitialAssignment: React.FC = () => {
  const {company} = useLayout()
  const [loading, setLoading] = useState(false)
  const [warehouseOptions, setWarehouseOptions] = useState<OptionType[]>([])
  const [productOptions, setProductOptions] = useState<OptionType[]>([])
  const [warehouse, setWarehouse] = useState<OptionType>({label: 'Select Warehouse...', value: 0})
  const [product, setProduct] = useState<OptionType>({label: 'Select Product...', value: 0})

  const initialValues: InitialAssignmentFormValues = {
    warehouse_id: 0,
    product_id: 0,
    quantity: 0,
    price_per_unit: 0,
    ref_doc_no: '',
    addition_date: getTodayDate(),
  }

  const formik = useFormik<InitialAssignmentFormValues>({
    initialValues,
    validationSchema: initialAssignmentSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      setLoading(true)
      const body = {
        stock_assignment_id: null,
        company_id: Number(company?.value) || null,
        warehouse_id: values.warehouse_id,
        product_id: values.product_id,
        quantity: values.quantity,
        price_per_unit: values.price_per_unit || 0,
        ref_doc_no: values.ref_doc_no || 'INITIAL',
        addition_date: values.addition_date,
      }
      await createStockAssignment(body)
        .then((res) => {
          res?.detail && toast.success('Initial stock has been assigned successfully.')
          formik.resetForm()
          setWarehouse({label: 'Select Warehouse...', value: 0})
          setProduct({label: 'Select Product...', value: 0})
        })
        .catch((err) => toast.error(err?.detail || err.message))
        .finally(() => setLoading(false))
    },
  })

  // Load warehouse and product options
  useEffect(() => {
    const loadOptions = async () => {
      if (!company?.value) return

      const [whRes, prodRes] = await Promise.all([
        getWarehouseOptions(company.value),
        getProductOptions(company.value),
      ])
      if (whRes) {
        setWarehouseOptions(
          whRes.map((w: WarehouseOption) => ({label: w.warehouse_name, value: w.warehouse_id}))
        )
      }
      if (prodRes) {
        setProductOptions(
          prodRes.map((p: ProductOption) => ({label: p.product_name, value: p.product_id}))
        )
      }
    }

    loadOptions()
  }, [company?.value])

  // Sync selects back into formik
  useEffect(() => {
    formik.setFieldValue('warehouse_id', warehouse.value, true)
    formik.setFieldValue('product_id', product.value, true)
  }, [warehouse.value, product.value])

  return (
    <KTCard>
      <div className='card-header border-0'>
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>Initial Stock Assignment</h3>
        </div>
      </div>

      <KTCardBody className='py-4'>
        <form onSubmit={formik.handleSubmit} noValidate className='form'>
          <div className='row mb-6'>
            <label className='col-lg-2 col-form-label required fw-bold fs-6'>Warehouse</label>
            <div className='col-lg-4 fv-row'>
              <Select
                value={warehouse}
                onChange={(option: any) => setWarehouse(option as OptionType)}
                options={warehouseOptions}
                isSearchable={true}
                className='fw-bold'
                styles={solidSelectStyles}
                components={removeIndicatorSeparator}
              />
            </div>

            <label className='col-lg-2 col-form-label required fw-bold fs-6'>Product</label>
            <div className='col-lg-4 fv-row'>
              <Select
                value={product}
                onChange={(option: any) => setProduct(option as OptionType)}
                options={productOptions}
                isSearchable={true}
                className='fw-bold'
                styles={solidSelectStyles}
                components={removeIndicatorSeparator}
              />
            </div>
          </div>

          <div className='row mb-6'>
            <label className='col-lg-2 col-form-label required fw-bold fs-6'>
              Initial Quantity
            </label>
            <div className='col-lg-4 fv-row'>
              <input
                type='number'
                className='form-control form-control-lg form-control-solid'
                placeholder='Initial Quantity'
                {...formik.getFieldProps('quantity')}
              />
              {formik.touched.quantity && formik.errors.quantity && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>{formik.errors.quantity}</div>
                </div>
              )}
            </div>

            <label className='col-lg-2 col-form-label fw-bold fs-6'>
              Valuation / Unit (&#8377;)
            </label>
            <div className='col-lg-4 fv-row'>
              <input
                type='number'
                className='form-control form-control-lg form-control-solid'
                placeholder='Inventory Valuation Per Unit'
                {...formik.getFieldProps('price_per_unit')}
              />
              {formik.touched.price_per_unit && formik.errors.price_per_unit && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>{formik.errors.price_per_unit}</div>
                </div>
              )}
            </div>
          </div>

          <div className='row mb-6'>
            <label className='col-lg-2 col-form-label fw-bold fs-6'>Reference Doc No</label>
            <div className='col-lg-4 fv-row'>
              <input
                type='text'
                className='form-control form-control-lg form-control-solid'
                placeholder='Reference Document No'
                {...formik.getFieldProps('ref_doc_no')}
              />
            </div>

            <label className='col-lg-2 col-form-label required fw-bold fs-6'>
              Date of Addition
            </label>
            <div className='col-lg-4 fv-row'>
              <input
                type='date'
                className='form-control form-control-lg form-control-solid'
                {...formik.getFieldProps('addition_date')}
              />
              {formik.touched.addition_date && formik.errors.addition_date && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>{formik.errors.addition_date}</div>
                </div>
              )}
            </div>
          </div>

          <div className='card-footer d-flex justify-content-start px-0 border-0 pb-0 pt-4'>
            <button type='submit' className='btn btn-primary' disabled={loading || !formik.isValid}>
              {!loading ? 'Add Initial Stock' : null}
              {loading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Please wait...{' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </form>
      </KTCardBody>
    </KTCard>
  )
}
